import { useState, useEffect } from 'react'

type UseLocalStorage = (key: string, initialValue?: any) => [any, Function]

/**
 * Sometimes users dont have local storage available and so this is a
 * best-effort attempt to save some state across pages
 *
 * @note wont work across page loads/reloads
 */
const tempStorage: Record<string, any> = {}

// React hook - modified from https://usehooks.com/useLocalStorage/
export const useLocalStorage: UseLocalStorage = (key, initialValue = null) => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof window === 'undefined') {
      return initialValue ?? {}
    }
    try {
      if (tempStorage[key]) {
        return tempStorage[key]
      }

      // Get from local storage by key
      const item = window.localStorage.getItem(key)

      // to initialise
      if (!item) {
        window.localStorage.setItem(key, JSON.stringify(initialValue))
      }

      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      // If error also return initialValue
      return tempStorage[key] ?? initialValue ?? {}
    }
  })
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: any) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value
      // Save state
      setStoredValue(valueToStore)
      // Save to local storage
      if (typeof window !== 'undefined') {
        tempStorage[key] = valueToStore
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      return tempStorage[key] ?? initialValue ?? {}
    }
  }

  // Initial render always returns empty, to mirror server
  const [returnedValue, setReturnedValue] = useState({})

  useEffect(() => {
    setReturnedValue(storedValue)
  }, [storedValue])

  return [returnedValue, setValue]
}
